import { Controller, useWatch, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'primereact/calendar'
import { resolveFieldError } from '../../../lib/forms/messages'

/** 24 saat biçimi "HH:mm" (00:00–23:59) — FormTimeField ile aynı. */
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

/** "HH:mm" → Date (bugünün tarihinde o saat/dakika); boş/geçersiz → null. */
function parseTime(value: string | null | undefined): Date | null {
  if (!value || !TIME_RE.test(value)) return null
  const [h, m] = value.split(':').map(Number)
  const d = new Date()
  d.setHours(h, m, 0, 0)
  return d
}

/** Date → "HH:mm" (24 saat, sıfır dolgulu); null → "". */
function formatTime(date: Date | null): string {
  if (!date) return ''
  const h = String(date.getHours()).padStart(2, '0')
  const m = String(date.getMinutes()).padStart(2, '0')
  return `${h}:${m}`
}

/** Şu anki saat "HH:mm" (opsiyonel saat ofseti; saniye/dk kırpılmaz). */
function nowTime(hourOffset = 0): string {
  const d = new Date()
  d.setHours(d.getHours() + hourOffset, d.getMinutes(), 0, 0)
  return formatTime(d)
}

/**
 * RHF-bağlı **saat aralığı** alanı — tek ızgara hücresinde yan yana (yarı yarıya) başlangıç ve bitiş saati
 * ({@code FormTimeField} ikiziyle aynı parse/format + PrimeReact `Calendar` timeOnly). Ortak bir başlık altında
 * iki `Calendar`; her biri kendi hatasını altında gösterir (aralık kuralı bitiş alanına düşer). Form değerleri
 * "HH:mm" string tutulur.
 */
interface FormTimeRangeFieldProps<T extends FieldValues> {
  control: Control<T>
  startName: FieldPath<T>
  endName: FieldPath<T>
  label: string
  startPlaceholder: string
  endPlaceholder: string
  required?: boolean
}

export default function FormTimeRangeField<T extends FieldValues>({
  control,
  startName,
  endName,
  label,
  startPlaceholder,
  endPlaceholder,
  required,
}: Readonly<FormTimeRangeFieldProps<T>>) {
  const { t } = useTranslation()
  const startValue = useWatch({ control, name: startName }) as string | null | undefined

  // Takvim açıldığında (onShow) boş alanı makul bir varsayılanla doldur ki kullanıcı sıfırdan başlamasın:
  // başlangıç → şu an; bitiş → seçili başlangıç + 1 saat (yoksa şu an + 1 saat).
  const defaultFor = (isEnd: boolean): string => {
    if (!isEnd) return nowTime()
    const s = parseTime(startValue)
    if (s) {
      s.setHours(s.getHours() + 1)
      return formatTime(s)
    }
    return nowTime(1)
  }

  const renderCalendar = (name: FieldPath<T>, placeholder: string, isEnd: boolean) => (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <div className="ui-time-range-item">
          <Calendar
            inputId={name}
            value={parseTime(field.value)}
            onChange={(e) => field.onChange(formatTime((e.value as Date | null) ?? null))}
            onShow={() => {
              if (!field.value) field.onChange(defaultFor(isEnd))
            }}
            onBlur={field.onBlur}
            placeholder={placeholder}
            timeOnly
            hourFormat="24"
            showIcon
            icon="pi pi-clock"
            className={fieldState.error ? 'p-invalid' : undefined}
          />
          {fieldState.error && (
            <div className="ui-field-err">{resolveFieldError(fieldState.error?.message, t)}</div>
          )}
        </div>
      )}
    />
  )

  return (
    <div className="ui-field">
      <span className="ui-field-label">
        {label} {required && <span className="ui-req">*</span>}
      </span>
      <div className="ui-time-range">
        {renderCalendar(startName, startPlaceholder, false)}
        {renderCalendar(endName, endPlaceholder, true)}
      </div>
    </div>
  )
}
